/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Authentication context provider managing global auth state.
Handles user sessions, permissions, and authentication-related operations.
*/

import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User as SupabaseUser, Session } from "@supabase/supabase-js";

export type UserRole = "admin" | "user";

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  createdAt: Date;
}

const mapToSupabaseRole = (role: UserRole): UserRole => {
  return role;
};

const mapFromSupabaseRole = (role: UserRole): UserRole => {
  return role;
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  createUser: (userData: { email: string; name: string; role: UserRole; password: string; organization_id?: string }) => Promise<void>;
  updateUser: (id: string, userData: Partial<User>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  useEffect(() => {
    // Set up auth state change listener FIRST (before checking current session)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log("Auth state changed:", event);
        setSession(newSession);
        setSupabaseUser(newSession?.user ?? null);

        if (newSession?.user) {
          // Use setTimeout to avoid potential issues with Supabase auth state updates
          setTimeout(() => {
            fetchUserProfile(newSession.user.id);
          }, 0);
        } else {
          setUser(null);
          setIsLoading(false); // Make sure to update loading state even when user is null
        }
      }
    );

    // THEN check for initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setSupabaseUser(initialSession?.user ?? null);
      
      if (initialSession?.user) {
        fetchUserProfile(initialSession.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error("Error fetching user profile:", error);
        setIsLoading(false);
        return;
      }

      if (data) {
        setUser({
          id: data.id,
          email: supabaseUser?.email || "",
          name: data.username,
          role: mapFromSupabaseRole(data.role as UserRole),
          createdAt: new Date(data.created_at),
        });
      }
    } catch (error) {
      console.error("Error in fetchUserProfile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<void> => {
    try {
      console.log("Attempting login with username:", username);
      setIsLoading(true);
      
      // Skip the database query and go straight to login with derived email
      const derivedEmail = `${username.toLowerCase().replace(/\s+/g, '_')}@mail.com`;
      console.log("Attempting login with derived email:", derivedEmail);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: derivedEmail,
        password,
      });
      
      if (error) {
        console.error("Login failed:", error);
        throw new Error(`Login failed. Please check your username and password and try again.`);
      }
      
      console.log("Login successful for:", username);
      toast.success(`Welcome back! Good to see you again.`);
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error.message || "Login failed. Please check your credentials and try again.");
      setIsLoading(false); // Important to reset loading state on error
      throw error;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("You've been logged out. Cheerio!");
      console.log("User logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Logout failed. Please try again.");
    }
  };

  const createUser = async (userData: { 
    email: string; 
    name: string; 
    role: UserRole; 
    password: string;
    organization_id?: string;
  }): Promise<void> => {
    try {
      console.log("Creating new user via edge function:", userData.email);
      console.log("User data with organization:", JSON.stringify(userData, null, 2));
      
      if (!user || user.role !== "admin") {
        throw new Error("Only administrators can create new users");
      }
      
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        throw new Error("No active session. Please log in again.");
      }

      // Ensure organization_id is included in the request body
      const requestBody = {
        email: userData.email,
        password: userData.password,
        username: userData.name,
        role: mapToSupabaseRole(userData.role),
        ...(userData.organization_id && { organization_id: userData.organization_id })
      };
      
      console.log("Request body being sent to edge function:", JSON.stringify(requestBody, null, 2));
      
      const { data, error } = await supabase.functions.invoke("create-user", {
        body: requestBody,
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`
        }
      });
      
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to create user through edge function");
      }
      
      if (!data || data.error) {
        throw new Error(data?.error || "Unknown error creating user");
      }
      
      console.log("Edge function response:", JSON.stringify(data, null, 2));
      console.log("User created successfully via edge function:", userData.name);
      toast.success(`New user ${userData.name} created. Brilliant!`);
    } catch (error: any) {
      console.error("Create user error:", error);
      toast.error(error.message || "Failed to create user. Please try again.");
      throw error;
    }
  };

  const updateUser = async (id: string, userData: Partial<User>): Promise<void> => {
    try {
      console.log("Updating user:", id);
      
      // Check if user exists and has permission to update
      if (!user) {
        throw new Error("You must be logged in to update users");
      }
      
      // Get the target user's role
      const { data: targetUser, error: targetError } = await supabase
        .from('users')
        .select('role')
        .eq('id', id)
        .single();
        
      if (targetError) throw targetError;
      
      // Prevent admins from editing other admins
      if (targetUser?.role === 'admin' && user.id !== id) {
        throw new Error("Admins cannot edit other admin users");
      }
      
      // Only allow admins to edit other users or users to edit themselves
      if (user.role !== 'admin' && user.id !== id) {
        throw new Error("You don't have permission to update this user");
      }
      
      const updateData: { username?: string; role?: UserRole } = {};
      
      if (userData.name) {
        updateData.username = userData.name;
      }
      
      if (userData.role) {
        updateData.role = mapToSupabaseRole(userData.role);
      }
      
      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
      
      if (user && user.id === id) {
        setUser({
          ...user,
          ...userData,
        });
      }
      
      console.log("User updated successfully");
      toast.success(`User details updated. All sorted!`);
    } catch (error: any) {
      console.error("Update user error:", error);
      toast.error(error.message || "Failed to update user. Please try again.");
      throw error;
    }
  };

  const deleteUser = async (id: string): Promise<void> => {
    try {
      console.log("Attempting to delete user:", id);
      
      if (!user || user.role !== "admin") {
        throw new Error("Only administrators can delete users");
      }
      
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        throw new Error("No active session. Please log in again.");
      }
      
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: {
          userId: id
        },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`
        }
      });
      
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to delete user through edge function");
      }
      
      if (!data || data.error) {
        throw new Error(data?.error || "Unknown error deleting user");
      }
      
      console.log("User deleted successfully via edge function");
      toast.success(`User account removed. Done and dusted!`);
    } catch (error: any) {
      console.error("Delete user error:", error);
      toast.error(error.message || "Failed to delete user. Please try again.");
      throw error;
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    createUser,
    updateUser,
    deleteUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  
  return context;
};
